import {FC} from 'react';
import {Form, Formik, FormikProps} from 'formik';
import {Box, Grid} from '@mui/material';
import {useIntl} from 'react-intl';
import {AMPInput} from '../../common/AMPInput';
import {getHelperText} from '../../../utils/forms';
import {CancelButton} from '../../common/CancelButton';
import {SubmitButton} from '../../common/SubmitButton';
import {NO_EMPLOYEE_OPTION, updateRfidFormSchema, UpdateRfidFormType} from '../formData/rfid/rfid';
import {rfidStatuses} from '../formData/common/initialData';
import {UpdateRfidDTO} from '../../../types';
import {UpdateRfidFormProps} from './UpdateRfidForm.types';
import {AMPFormSelect} from '../../common/AMPFormSelect';
import {CompanyEmployeeSelect} from '../CompanyEmployeeSelect';
import {BlockStatusEnum} from '../../common/RfidTable/RfidTable.types';
import {actionsWrapperSx, formContentSx, formSx} from './UpdateRfidForm.sx';

export const UpdateRfidForm: FC<UpdateRfidFormProps> = ({
                                                            handleClose,
                                                            handleSubmit,
                                                            initialValues,
                                                            companyId,
                                                            businessId,
                                                        }) => {
    const intl = useIntl();
    const isCompanyTag = Boolean(companyId);

    const onSubmit = () => {
        return (values: UpdateRfidFormType) => {
            const res: UpdateRfidDTO = {
                blocked: values.blocked !== BlockStatusEnum.accepted,
                idTag: values.idTag,
                licensePlate: values.licensePlate?.trim() ? values.licensePlate.trim() : null,
            };
            if (isCompanyTag) {
                res.employeeId =
                    values.employeeId && values.employeeId !== NO_EMPLOYEE_OPTION
                        ? +values.employeeId
                        : null;
            }
            handleSubmit(res);
            handleClose();
        };
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={updateRfidFormSchema}
            onSubmit={onSubmit()}
        >
            {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  touched,
                  values,
              }: FormikProps<UpdateRfidFormType>) => (
                <Form
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <Box sx={formSx}>
                        <Box sx={formContentSx}>
                            <Grid container>
                                <Grid
                                    xs={12}
                                    item
                                >
                                    <AMPInput
                                        value={values['idTag']}
                                        type={'idTag'}
                                        label={'rfidTag'}
                                        input={'idTag'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched['idTag'] && Boolean(errors['idTag'])}
                                        helperText={getHelperText('idTag', touched, errors, intl)}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container>
                                <Grid
                                    xs={12}
                                    item
                                >
                                    <AMPInput
                                        value={values['licensePlate']}
                                        type={'text'}
                                        label={'licensePlate'}
                                        input={'licensePlate'}
                                        placeholder={'licensePlatePlaceholder'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched['licensePlate'] && Boolean(errors['licensePlate'])}
                                        helperText={getHelperText('licensePlate', touched, errors, intl)}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container>
                                <Grid
                                    xs={12}
                                    item
                                >
                                    <AMPFormSelect
                                        data={rfidStatuses(intl)}
                                        value={values['blocked']}
                                        label={'status'}
                                        input={'blocked'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched['blocked'] && Boolean(errors['blocked'])}
                                        helperText={getHelperText('blocked', touched, errors, intl)}
                                    />
                                </Grid>
                            </Grid>
                            {isCompanyTag && (
                                <Grid container>
                                    <Grid
                                        xs={12}
                                        item
                                    >
                                        <CompanyEmployeeSelect
                                            businessId={businessId}
                                            companyId={companyId}
                                            value={values['employeeId']}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={touched['employeeId'] && Boolean(errors['employeeId'])}
                                            helperText={getHelperText('employeeId', touched, errors, intl)}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </Box>
                        <Box sx={actionsWrapperSx}>
                            <CancelButton
                                text="cancel"
                                handler={handleClose}
                            />
                            <SubmitButton
                                text="save"
                                color="primary"
                                isSubmitting={isSubmitting}
                            />
                        </Box>
                    </Box>
                </Form>
            )}
        </Formik>
    );
};
